/**
 * @file ShareButton.jsx
 * @description A button component for actions (edit, duplicate).
 * @requires React, PropTypes, semantic-ui-react
 * @exports ShareButton
 */

import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Icon,
    Popup,
    SemanticCOLORS,
    SemanticICONS,
} from 'semantic-ui-react'
import {
    FacebookShareButton,
    EmailShareButton,
    TwitterShareButton,
    WhatsappShareButton,
    FacebookIcon,
    EmailIcon,
    TwitterIcon,
    WhatsappIcon,
    InstapaperShareButton,
    ThreadsShareButton,
    FacebookMessengerShareButton,
    FacebookMessengerIcon,
    ThreadsIcon,
    InstapaperIcon,
} from 'react-share'

import './ActionButtons.css'

/**
 * handleElementAction is a helper function that prevents the default redirection behavior of an element.
 *
 * @function handleElementAction
 * @param {React.SyntheticEvent} element Element to prevent redirection.
 * @param {Function} action Function to perform the action (edit, duplicate, delete).
 * @param {string} id Element ID to be passed for the respective action.
 * @returns {void}
 *
 * @example
 * handleElementAction(eventElement.id, handleEditEvent);
 */
const handleElementAction = async (element, action, id) => {
    element.stopPropagation() // Disables the redirection from the div zone
    if (action) {
        action(id)
    }
}

/**
 * @typedef {Object} ActionButtonProps
 * @property {Function} [action] - Function to perform the action (edit, duplicate). Optional when used as share-only button.
 * @property {string} [elementId] - Element ID to be passed for the respective action. Required only when action is provided.
 * @property {string} label - Button aria label.
 * @property {string} icon - Button icon name.
 * @property {string} tooltipText - Tooltip text.
 * @property {SemanticCOLORS | "facebook" | "google plus" | "vk" | "twitter" | "linkedin" | "instagram" | "youtube"} [color='blue'] - Button color.
 */

/**
 * ShareButton is a reusable component for sharing content via social media.
 *
 * @component
 * @param {ActionButtonProps} props Component properties.
 * @returns {JSX.Element} A styled button component with share options on hover.
 *
 * @example
 * <ShareButton
 *    label="Share this event"
 *    icon="share"
 *    tooltipText={'Share'}
 * />
 */
const ShareButton: React.FC<{
    action?: (id: string) => void
    elementId?: string
    label: string
    icon: SemanticICONS
    tooltipText: string
    customClassName: string
    url?: string
}> = ({
    action,
    elementId,
    label,
    icon,
    tooltipText,
    customClassName,
    url = window.location.href, // Default to current URL if not provided
}) => {
    // Validate that if action is provided, elementId is also provided
    if (action && !elementId) {
        throw new Error(
            "ShareButton: When 'action' is provided, 'elementId' is required."
        )
    }
    const [shareUrl, setShareUrl] = React.useState(
        `https://fablia.fr/Story/${elementId ? elementId : 'url not copied'}`
    )
    useEffect(() => {
        // Check if the URL contains '/SelectStory' and set shareUrl accordingly
        if (!url.includes('/SelectStory')) {
            setShareUrl(url)
        }
    }, [url])

    // Function to handle copying URL to clipboard
    const handleCopyShare = (event?: React.MouseEvent) => {
        if (event) event.stopPropagation();
        navigator.clipboard
            .writeText(shareUrl)
            .then(() => {
                alert('Link copied to clipboard!')
            })
            .catch((err) => {
                console.error('Failed to copy: ', err)
                // Fallback for browsers that don't support clipboard API
                const textArea = document.createElement('textarea')
                textArea.value = shareUrl
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                document.body.removeChild(textArea)
                alert('Link copied to clipboard!')
            })
    }

    // Instagram sharing via popup with copy functionality
    const handleInstagramShare = (event?: React.MouseEvent) => {
        if (event) event.stopPropagation();
        handleCopyShare(event)
    }

    // SMS sharing via URL scheme
    const handleSmsShare = (event?: React.MouseEvent) => {
        if (event) event.stopPropagation();
        // Create SMS URL with pre-populated message including the share URL
        const message = encodeURIComponent(
            `Check this out: ${elementId ? elementId : 'url not copied'}`
        )
        const smsUrl = `sms:?&body=${message}`

        // Open the SMS app with the pre-filled message
        window.open(smsUrl, '_blank')
    }

    return (
        <Popup
            trigger={
                <Button
                    className={customClassName}
                    onClick={(element) =>{
                        element.stopPropagation()
                        handleElementAction(element, action, elementId)
                    }}
                    icon
                    aria-label={label}
                >
                    <Icon name={icon} />
                </Button>
            }
            content={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        gap: '10px',
                        flexWrap: 'wrap',
                    }}
                >
                    <Popup
                        content="Share on Facebook"
                        trigger={
                            <div onClick={e => e.stopPropagation()}>
                                <FacebookShareButton url={shareUrl}>
                                    <FacebookIcon size={32} round />
                                </FacebookShareButton>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                    <Popup
                        content="Share via Email"
                        trigger={
                            <div onClick={e => e.stopPropagation()}>
                                <EmailShareButton url={shareUrl}>
                                    <EmailIcon size={32} round />
                                </EmailShareButton>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                    <Popup
                        content="Share on Twitter"
                        trigger={
                            <div onClick={e => e.stopPropagation()}>
                                <TwitterShareButton url={shareUrl}>
                                    <TwitterIcon size={32} round />
                                </TwitterShareButton>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                    <Popup
                        content="Share on Threads"
                        trigger={
                            <div onClick={e => e.stopPropagation()}>
                                <ThreadsShareButton url={shareUrl}>
                                    <ThreadsIcon size={32} round />
                                </ThreadsShareButton>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                    <Popup
                        content="Share on WhatsApp"
                        trigger={
                            <div onClick={e => e.stopPropagation()}>
                                <WhatsappShareButton url={shareUrl}>
                                    <WhatsappIcon size={32} round />
                                </WhatsappShareButton>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                    <Popup
                        content="Share via SMS"
                        trigger={
                            <div>
                                <Button
                                    className="social-media-button"
                                    onClick={handleSmsShare}
                                    style={{
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: '50%',
                                        backgroundColor: '#5BC236',
                                    }}
                                >
                                    <Icon
                                        name="comment"
                                        style={{ color: 'white', margin: 0 }}
                                    />
                                </Button>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                    <Popup
                        content="Copy link for Instagram"
                        trigger={
                            <div>
                                <Button
                                    className="social-media-button"
                                    onClick={handleInstagramShare}
                                    style={{
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: '50%',
                                        background:
                                            'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                    }}
                                >
                                    <Icon
                                        name="instagram"
                                        style={{ color: 'white', margin: 0 }}
                                    />
                                </Button>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                    <Popup
                        content="Copy link to clipboard"
                        trigger={
                            <div>
                                <Button
                                    className="social-media-button"
                                    onClick={handleCopyShare}
                                    style={{
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: '50%',
                                        backgroundColor: '#808080',
                                    }}
                                >
                                    <Icon
                                        name="copy"
                                        style={{ color: 'white', margin: 0 }}
                                    />
                                </Button>
                            </div>
                        }
                        position="top center"
                        size="tiny"
                        inverted
                    />
                </div>
            }
            on="click"
        />
    )
}

ShareButton.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    action: PropTypes.func, // Now optional
    elementId: PropTypes.string, // Now optional
    tooltipText: PropTypes.string.isRequired,
    color: PropTypes.string,
}

export default ShareButton
